import { Injectable, Output, EventEmitter } from "@angular/core";
import { FilterTemplateResponseModel } from "../models/filter-template.response.model";
import {
  AssistedByFilterOption,
  AssistTypeFilterOption,
  AutoWrapUpStatusFilterOption,
  CategoryFilterOption,
  DayOfWeekFilterOption,
  FiltersOptionsModel,
  LocationFilterOption,
  RequestedStaffFilterOption,
  SearchTermsFilterOption,
  ServiceFilterOption,
  StatusFilterOption,
  SupervisorFilterOption,
  TimePeriodFilterOption,
  ViewFilterOption,
  UserFilterOption,
} from "../models/filter-options-model";
import { NavigationStart, Router } from "@angular/router";
import { filter } from "rxjs/internal/operators/filter";
import { LobbyDetailViewHeaderDataResponseModel } from "../models/lobby-data-view.response.model";
import { ValueAxisModel } from "../models/value-axis.model";
import { Subscription } from "rxjs";
import { getSystemConfigurations } from "../../../state";
import { Store } from "@ngrx/store";
import { GroupResult, State } from "@progress/kendo-data-query";
import { ReportService } from "./report.service";
import { IsaveAs } from "../models/saveAs-interface.model";
import { DetailView } from "src/app/shared/enums/detailView.enum";
import { readCastedValue } from "src/app/shared/utils/read-casted-value.function";
import { TranslateService } from "@ngx-translate/core";
import { DayOfWeek } from "src/app/admin/user/shared/models/day.enum";

enum TypesForReconcile {
  Locations = 0,
  TimePeriods = 1,
  DaysOfWeek = 2,
  RequestedStaff = 3,
  AssistedBys,
  Supervisors,
  Categories,
  Services,
  Statuses,
  AssistTypes,
  Views,
  AutoWrapUpStatuses,
  Users,
}
enum MappingDirection {
  TemplateToOptions = 0,
  OptionsToTemplate = 1,
}

@Injectable({
  providedIn: "root",
})
export class ReportCommonService {
  //#region Variable Declaration

  public currentTemplate: FilterTemplateResponseModel =
    new FilterTemplateResponseModel();
  public currentSelectedOptions: FiltersOptionsModel =
    new FiltersOptionsModel();
  public templates: FilterTemplateResponseModel[] =
    new Array<FilterTemplateResponseModel>();
  public originalTemplates: FilterTemplateResponseModel[] =
    new Array<FilterTemplateResponseModel>();
  public options: FiltersOptionsModel = new FiltersOptionsModel();
  public locations: LocationFilterOption[] = new Array<LocationFilterOption>();
  public optionsPageFormData: any;
  public useTimePeriod = false;
  public savedViewName: string = "";
  public reportStartDate: Date = new Date();
  public reportEndDate: Date = new Date();
  public maxReportDate: Date = new Date();
  public minReportDate: Date = new Date();
  public lobbyReportHeaderData: LobbyDetailViewHeaderDataResponseModel =
    new LobbyDetailViewHeaderDataResponseModel();
  public trafficAndWaitViewDataLoaded: boolean = false;
  public locationComparisonViewDataLoaded: boolean = false;
  public productsAndServicesViewDataLoaded: boolean = false;
  public employeeViewDataLoaded: boolean = false;
  public headerDataLoaded: boolean = false;
  public runReportExecuted: boolean = false;
  public lobbyConfiguration: any;
  private lobbyConfigurationSubscriptions = new Subscription();

  // Data structures to populate charts
  public avgTrafficWaitByDayValueAxis: ValueAxisModel[] = [];
  public avgTrafficWaitByDayCategories: string[] = [];
  public avgTrafficWaitByDayCrossingValues: number[] = [];
  public avgTrafficWaitByDaySeriesData: any[] = [];
  public avgTrafficWaitByDayVisible: boolean = false;

  public avgTrafficWaitByWeekOfMonthValueAxis: ValueAxisModel[] = [];
  public avgTrafficWaitByWeekOfMonthCategories: string[] = [];
  public avgTrafficWaitByWeekOfMonthCrossingValues: number[] = [];
  public avgTrafficWaitByWeekOfMonthSeriesData: any[] = [];
  public avgTrafficWaitByWeekOfMonthVisible: boolean = false;

  public avgTrafficWaitByHourValueAxis: ValueAxisModel[] = [];
  public avgTrafficWaitByHourCategories: string[] = [];
  public avgTrafficWaitByHourCrossingValues: number[] = [];
  public avgTrafficWaitByHourSeriesData: any[] = [];
  public avgTrafficWaitByHourVisible: boolean = false;

  public avgTrafficWaitByMonthValueAxis: ValueAxisModel[] = [];
  public avgTrafficWaitByMonthCategories: string[] = [];
  public avgTrafficWaitByMonthCrossingValues: number[] = [];
  public avgTrafficWaitByMonthSeriesData: any[] = [];
  public avgTrafficWaitByMonthVisible: boolean = false;

  public trafficAndWaitByLocationValueAxis: ValueAxisModel[] = [];
  public trafficAndWaitByLocationCategories: string[] = [];
  public trafficAndWaitByLocationCrossingValues: number[] = [];
  public trafficAndWaitByLocationSeriesData: any[] = [];
  public trafficAndWaitByLocationVisible: boolean = false;

  public productsAndServicesByLocationValueAxis: ValueAxisModel[] = [];
  public productsAndServicesByLocationCategories: string[] = [];
  public productsAndServicesByLocationCrossingValues: number[] = [];
  public productsAndServicesByLocationSeriesData: any[] = [];
  public productsAndServicesByLocationVisible: boolean = false;

  public avgAssistTimeByLocationValueAxis: ValueAxisModel[] = [];
  public avgAssistTimeByLocationCategories: string[] = [];
  public avgAssistTimeByLocationCrossingValues: number[] = [];
  public avgAssistTimeByLocationSeriesData: any[] = [];
  public avgAssistTimeByLocationVisible: boolean = false;

  public productsAndServicesByEmployeeValueAxis: ValueAxisModel[] = [];
  public productsAndServicesByEmployeeCategories: string[] = [];
  public productsAndServicesByEmployeeCrossingValues: number[] = [];
  public productsAndServicesByEmployeeSeriesData: any[] = [];
  public productsAndServicesByEmployeeVisible: boolean = false;

  public avgAssistTimeByEmployeeValueAxis: ValueAxisModel[] = [];
  public avgAssistTimeByEmployeeCategories: string[] = [];
  public avgAssistTimeByEmployeeCrossingValues: number[] = [];
  public avgAssistTimeByEmployeeSeriesData: any[] = [];
  public avgAssistTimeByEmployeeVisible: boolean = false;

  public productsAndServicesProvidedValueAxis: ValueAxisModel[] = [];
  public productsAndServicesProvidedCategories: string[] = [];
  public productsAndServicesProvidedCrossingValues: number[] = [];
  public productsAndServicesProvidedSeriesData: any[] = [];
  public productsAndServicesProvidedVisible: boolean = false;

  public avgAssistTimeByProductOrServiceValueAxis: ValueAxisModel[] = [];
  public avgAssistTimeByProductOrServiceCategories: string[] = [];
  public avgAssistTimeByProductOrServiceCrossingValues: number[] = [];
  public avgAssistTimeByProductOrServiceSeriesData: any[] = [];
  public avgAssistTimeByProductOrServiceVisible: boolean = false;

  // charts zoom initial level
  public firstCategoryIndex: number = 0;
  public lastCategoryIndex: number = 0;

  public mappedEmployeeAssistSeries: GroupResult[] = [];
  public mappedLocationsAssistSeries: GroupResult[] = [];

  public viewsLocalizedKeys: Record<number, string> = {
    1: "_Report_View_Location_Comparison_Text_",
    2: "_Report_View_Traffic_And_Wait_Time_Text_",
    3: "_General_Products_Services_Text_",
    4: "_Report_View_Employee_Text_",
    5: "_Report_View_Details_Text_",
    6: "_Report_View_Staff_Signed_In_Out_Text_",
  };

  //#endregion Variable Declaration

  constructor(
    public router: Router,
    private store: Store<State>,
    public reportService: ReportService,
    private translateService: TranslateService
  ) {
    router.events
      .pipe(filter((e): e is NavigationStart => e instanceof NavigationStart))
      .subscribe((e: NavigationStart) => {
        if (e.url == "/report/lobby-detail-view") {
          this.initVars();
        }
      });
    this.lobbyConfigurationSubscriptions.add(
      this.store.select(getSystemConfigurations).subscribe((configurations) => {
        const lobbyConfigurations = configurations.filter(
          (c) => c.configurationGroup === "LobbySettings"
        );
        if (lobbyConfigurations.length > 0) {
          this.lobbyConfiguration = {
            enableAccountNumber: readCastedValue(
              lobbyConfigurations.find(
                (ac) => ac.configurationName === "EnableAccountNumber"
              )
            ),
            accountNumberText: readCastedValue(
              lobbyConfigurations.find(
                (x) => x.configurationName == "AccountNumberDisplayText"
              )
            ),
          };
        }
      })
    );
  }

  // Events
  @Output() applyClicked: EventEmitter<number> = new EventEmitter();
  @Output() cancelClicked: EventEmitter<boolean> = new EventEmitter();
  @Output() scrollTop: EventEmitter<boolean> = new EventEmitter();
  @Output() saveTemplateEvent: EventEmitter<string> = new EventEmitter();
  @Output() deleteTemplateEvent: EventEmitter<string> = new EventEmitter();
  @Output() saveTemplateAsEvent: EventEmitter<IsaveAs> = new EventEmitter();
  @Output() setTemplateAsDefaultEvent: EventEmitter<boolean> =
    new EventEmitter();

  public applyEventSubscribed = false;

  // Called on construction
  public initVars() {
    this.useTimePeriod = false;
    this.savedViewName = "";
    this.trafficAndWaitViewDataLoaded = false;
    this.locationComparisonViewDataLoaded = false;
    this.productsAndServicesViewDataLoaded = false;
    this.employeeViewDataLoaded = false;
    this.headerDataLoaded = false;
    this.runReportExecuted = false;
    this.avgTrafficWaitByDayVisible = false;
    this.avgTrafficWaitByWeekOfMonthVisible = false;
    this.avgTrafficWaitByHourVisible = false;
    this.avgTrafficWaitByMonthVisible = false;
    this.trafficAndWaitByLocationVisible = false;
    this.productsAndServicesByLocationVisible = false;
    this.avgAssistTimeByLocationVisible = false;
    this.productsAndServicesByEmployeeVisible = false;
    this.avgAssistTimeByEmployeeVisible = false;
    this.productsAndServicesProvidedVisible = false;
    this.avgAssistTimeByProductOrServiceVisible = false;
  }

  //#region Event emitters
  public saveTemplate(name: string) {
    this.saveTemplateEvent.emit();
  }
  public saveTemplateAs(obj: IsaveAs) {
    this.saveTemplateAsEvent.emit(obj);
  }
  public deleteTemplate(name: string) {
    this.deleteTemplateEvent.emit();
  }
  public setTemplateAsDefault(name: string) {
    this.setTemplateAsDefaultEvent.emit();
  }
  public filtersClosed() {
    this.cancelClicked.emit();
  }
  public applyOptions(detailViewId: number) {
    this.applyClicked.emit(detailViewId);
  }
  public emitScrollRequest() {
    this.scrollTop.emit();
  }

  //#endregion Event emitters

  // Template-to-Options Conversion
  public ConvertCurrentTemplateToOptions(detailViewId: number) {
    this.MapLocations(MappingDirection.TemplateToOptions, undefined);
    this.MapTimePeriods(MappingDirection.TemplateToOptions, undefined);
    this.MapDaysOfWeek(MappingDirection.TemplateToOptions, undefined);
    this.MapRequestedStaff(MappingDirection.TemplateToOptions, undefined);
    this.MapAssistedBys(MappingDirection.TemplateToOptions, undefined);
    this.MapSupervisors(MappingDirection.TemplateToOptions, undefined);
    this.MapCategories(MappingDirection.TemplateToOptions, undefined);
    this.MapServices(MappingDirection.TemplateToOptions, undefined);
    this.MapStatuses(MappingDirection.TemplateToOptions, undefined);
    this.MapAssistTypes(MappingDirection.TemplateToOptions, undefined);
    if (detailViewId == DetailView["Lobby_Activity"])
      this.MapViews(MappingDirection.TemplateToOptions, undefined);
    this.MapAutoWrapUpStatuses(MappingDirection.TemplateToOptions, undefined);
    this.MapSearchTerms(MappingDirection.TemplateToOptions, undefined);
    this.MapUsers(MappingDirection.TemplateToOptions, undefined);
  }
  // Options-to-Template Conversion
  public ConvertSelectedOptionsToTemplate(
    formData: {
      editListDropdown: string;
      isDefault: boolean;
      detailViewId: number;
    },
    detailViewId: number = 1
  ): FilterTemplateResponseModel {
    this.optionsPageFormData = formData;
    this.filterServicesByCategory(this.optionsPageFormData.categoryDropdown);
    this.filterAssistedByStaffBySupervisor(
      this.optionsPageFormData.supervisorDropdown
    );
    this.ConvertOptionsFormDataToSelectedOptions();
    let template = new FilterTemplateResponseModel();
    template = this.MapLocations(MappingDirection.OptionsToTemplate, template);
    template = this.MapTimePeriods(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapDaysOfWeek(MappingDirection.OptionsToTemplate, template);
    template = this.MapRequestedStaff(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapAssistedBys(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapSupervisors(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapCategories(MappingDirection.OptionsToTemplate, template);
    template = this.MapServices(MappingDirection.OptionsToTemplate, template);
    template = this.MapStatuses(MappingDirection.OptionsToTemplate, template);
    template = this.MapAssistTypes(
      MappingDirection.OptionsToTemplate,
      template
    );
    if (detailViewId == DetailView["Lobby_Activity"])
      template = this.MapViews(MappingDirection.OptionsToTemplate, template);
    template = this.MapAutoWrapUpStatuses(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapSearchTerms(
      MappingDirection.OptionsToTemplate,
      template
    );
    template = this.MapUsers(MappingDirection.OptionsToTemplate, template);
    template.templateName = "Custom"; // formData.editListDropdown;
    template.isDefault = formData.isDefault;
    template.detailViewId = formData.detailViewId;
    
    return template;
  }
  // Filter Options Window Form reader
  public ConvertOptionsFormDataToSelectedOptions() {
    this.options.selectedAssistTypes =
      this.optionsPageFormData.assistTypeDropdown;
    this.options.selectedAssistedBys =
      this.optionsPageFormData.assistedByDropdown;
    this.options.selectedCategories = this.optionsPageFormData.categoryDropdown;
    this.options.selectedDaysOfWeek =
      this.optionsPageFormData.dayOfWeekDropdown;
    this.options.selectedLocations = this.optionsPageFormData.locationDropdown;
    this.options.selectedRequestedStaff =
      this.optionsPageFormData.userRequestedDropdown;
    this.options.selectedServices = this.optionsPageFormData.serviceDropdown;
    this.options.selectedStatuses = this.optionsPageFormData.statusDropdown;
    this.options.selectedSupervisors =
      this.optionsPageFormData.supervisorDropdown;
    const tp = this.optionsPageFormData.timePeriodDropdown;
    this.options.selectedTimePeriod = Array.isArray(tp) ? tp : (tp ? [tp] : []);
    const vd = this.optionsPageFormData.viewDropdown;
    this.options.selectedViews = Array.isArray(vd) ? vd : (vd ? [vd] : []);
    const aw = this.optionsPageFormData.autoWrapUpStatusDropdown;
    this.options.selectedAutoWrapUpStatus = Array.isArray(aw) ? aw : (aw ? [aw] : []);
    this.options.selectedUsers = this.optionsPageFormData.signedInUsersDropdown;
    this.options.searchTerms = [
      ...this.options.searchTerms,
      {
        detailViewSearchTermId: -1,
        searchTerm: this.optionsPageFormData.search,
      },
    ];
    this.options.isDefault = this.optionsPageFormData.isDefault;
  }
  /* TemplatesAreEqual
   *  Utilizing the 'selectedXXXMatch' functions just below, this function does a deep compare between the submitted templates
   *  returning true/false based on their equivalency.
   *  Note: the 'selectedXXXMatch' do not compare EVERY property in their relevant arrays - just enough selected properties as to
   *  establish equivalency - see each 'selectedXXXMatch' function individually to see what criteria each uses.
   */
  public TemplatesAreEqual(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel,
    testForDefault: boolean = true
  ) {
    let result = true;
    if (
      first.allAssistTypes != second.allAssistTypes ||
      first.allAssistedBy != second.allAssistedBy ||
      first.allCategories != second.allCategories ||
      first.allDays != second.allDays ||
      first.allLocations != second.allLocations ||
      first.allRequestedStaff != second.allRequestedStaff ||
      first.allServices != second.allServices ||
      first.allStatuses != second.allStatuses ||
      first.allSupervisors != second.allSupervisors ||
      first.allUsers != second.allUsers ||
      first.detailViewId != second.detailViewId
    ) {
      result = false;
    } else {
      if (
        !this.SelectedAssistTypesMatch(first, second) ||
        !this.SelectedAssistedBysMatch(first, second) ||
        !this.SelectedCategoriesMatch(first, second) ||
        !this.SelectedDaysMatch(first, second) ||
        !this.SelectedLocationsMatch(first, second) ||
        !this.SelectedRequestedStaffMatch(first, second) ||
        !this.SelectedServicesMatch(first, second) ||
        !this.SelectedStatusesMatch(first, second) ||
        !this.SelectedSupervisorsMatch(first, second) ||
        !this.SelectedUsersMatch(first, second) ||
        (first.detailViewId == DetailView["Lobby_Activity"] &&
          !this.SelectedViewsMatch(first, second)) ||
        !this.SelectedAWStatusMatch(first, second)
      ) {
        result = false;
      }
    }
    if (
      !this.SelectedTimePeriodsMatch(first, second) ||
      !this.SelectedSearchTermsMatch(first, second)
    ) {
      result = false;
    }
    if (testForDefault && first.isDefault != second.isDefault) {
      result = false;
    }
    return result;
  }
  /* TemplateMatchesExisting
   * Calls TemplatesAreEqual (detailed above) to see if any template in the templates collection object
   * deep-matches the submitted template
   * returns the matching template if one found
   */
  public TemplateMatchesExisting(
    temp: FilterTemplateResponseModel,
    testForDefault: boolean,
    src?: FilterTemplateResponseModel[]
  ): FilterTemplateResponseModel {
    var result: FilterTemplateResponseModel = new FilterTemplateResponseModel();
    var source: FilterTemplateResponseModel[];
    if (src) {
      source = src;
    } else {
      source = this.templates;
    }
    var keys = Object.keys(source);
    for (var i = 0; i < keys.length; i++) {
      const sourceIndex = parseInt(keys[i]);
      if (
        temp.templateName == source[sourceIndex].templateName &&
        this.TemplatesAreEqual(source[sourceIndex], temp, testForDefault)
      ) {
        result = source[sourceIndex];
        break;
      }
    }
    return result;
  }
  /* TemplateMatchesCurrent
   *  Calls TemplatesAreEqual (detailed above) to see if any the current template in the templates collection object
   *  deep-matches the submitted template
   *  returns true/false based on the equivalency found
   */
  public TemplateMatchesCurrent(
    temp: FilterTemplateResponseModel,
    testForDefault: boolean
  ) {
    if (this.TemplatesAreEqual(this.currentTemplate, temp, testForDefault)) {
      return true;
    }
    return false;
  }

  // ReconcileTemplateWithOptions
  //
  // When a template comes back from the server, (from the GetAllTemplates call), it must not contain filters options (ActiveFilters) that do not match up
  // with the universe of options that are fetched from the server and will populate the filters drop downs (via reportService.getDetailsViewFilterOptions).
  // This function runs through the filters specified in the template(s) and makes sure every active filter listed
  // is represented in the options lists form the filter options drop downs. If a mismatch is detected, the local version
  // of the offending template is edited to resolve the issue (by removing the mismatched filter from the template).
  // An additional check is the 'all' flags...if a template comes in from the server that shows 'false' for a given 'all' flag (e.g. allLocations),
  // then it MUST have at least one location filter entry in its 'ActiveFilters' collection. However, this info may be out of date. Imagine that the
  // current user has been demoted from 'regional manager' to 'branch manager'. Say also that their valid location filters contained locations outside
  // their branch (which is the most likely outcome in such an example) - that WERE valid when the user WAS a regional manager. But, say that list of
  // locations filters only included, for example, 3 out of 10 possible locations. In such a case, the allLocations flag would be 'false', and the
  // list of locations to filter by would be correct. NOW, however, the user has access ONLY to 1 location - their branch. In this case, the template would come
  // from the server in an invalid state - it would still contain the other locations from the previous status the user had as a regional manager.
  // This inconsistency will be discovered when the options lists are retruned from the server, since this list is ALWAYS up-to-date with the CURRENT
  // access levels of the user. So - the options collection will not contain any possible locations outside the user's current branch.
  // ReconcileTemplateWithOptions will detect this and remove the invalid filters from the template so that accurate comaprisons and validations can
  // be made when validating the filters prior to issuing data queries. One last inconsistency that the above scenario will reveal in the ReconcileTemplateWithOptions
  // function is that the 'allLocations' flag is now wrong - since, due to the fact that only one location is possible, and this template has selected it.
  // Therefore, this additional fact will be detected by ReconcileTemplateWithOptions - and the allLocations flag will be set to 'true', and the selection
  // in the ActiveFilters locations array will be cleared.
  // This 'correction is NOT posted back to the server, so any errors in the template on the server must be corrected with
  // another process. That is for another release.
  public ReconcileTemplateWithOptions(kind: TypesForReconcile) {
    const activeFiltersInTemplate = this.currentTemplate.activeFilters;
    switch (kind) {
      case TypesForReconcile.Locations:
        let reconciledLocations = new Array<LocationFilterOption>();
        activeFiltersInTemplate.locations.forEach((l) => {
          if (this.options.locations.find((o) => o.code == l.code)) {
            reconciledLocations = [...reconciledLocations, l];
          }
        });
        if (reconciledLocations.length == this.options.locations.length) {
          this.currentTemplate.allLocations = true;
          activeFiltersInTemplate.locations = [];
        } else if (reconciledLocations.length == 0 && activeFiltersInTemplate.locations.length > 0) {
          this.currentTemplate.allLocations = true;
          activeFiltersInTemplate.locations = [];
        } else {
          activeFiltersInTemplate.locations = reconciledLocations;
        }
        break;
      case TypesForReconcile.TimePeriods:
        let reconciledTimePeriods = new Array<TimePeriodFilterOption>();
        activeFiltersInTemplate.timePeriods.forEach((tp) => {
          if (
            this.options.timePeriods.find(
              (o) => o.detailViewTimePeriodId == tp.detailViewTimePeriodId
            )
          ) {
            reconciledTimePeriods = [...reconciledTimePeriods, tp];
          }
        });
        activeFiltersInTemplate.timePeriods = reconciledTimePeriods;
        break;
      case TypesForReconcile.DaysOfWeek:
        let reconciledDaysOfWeek = new Array<DayOfWeekFilterOption>();
        activeFiltersInTemplate.daysOfWeek.forEach((dow) => {
          let day = this.options.daysOfWeek.find(
            (o) => o.dayOfWeek == dow.dayOfWeek
          );
          if (day) {
            dow.localizedDayOfWeekName = day.localizedDayOfWeekName;
            reconciledDaysOfWeek = [...reconciledDaysOfWeek, dow];
          }
        });
        if (reconciledDaysOfWeek.length == this.options.daysOfWeek.length) {
          this.currentTemplate.allDays = true;
          activeFiltersInTemplate.daysOfWeek = [];
        } else {
          activeFiltersInTemplate.daysOfWeek = reconciledDaysOfWeek;
        }
        break;
      case TypesForReconcile.RequestedStaff:
        let reconciledRequestedStaff = new Array<RequestedStaffFilterOption>();
        activeFiltersInTemplate.requestedStaff.forEach((stf) => {
          let staff = this.options.requestedStaff.find(
            (o) =>
              o.code == stf.code &&
              o.isNotificationGroup == stf.isNotificationGroup
          );
          if (staff) {
            stf.localizedStaffFullname = staff.localizedStaffFullname;
            stf.isNotificationGroup = staff.isNotificationGroup;
            reconciledRequestedStaff = [...reconciledRequestedStaff, stf];
          }
        });
        if (
          reconciledRequestedStaff.length == this.options.requestedStaff.length
        ) {
          this.currentTemplate.allRequestedStaff = true;
          activeFiltersInTemplate.requestedStaff = [];
        } else {
          activeFiltersInTemplate.requestedStaff = reconciledRequestedStaff;
        }
        break;
      case TypesForReconcile.AssistedBys:
        let reconciledAssistedBy = new Array<AssistedByFilterOption>();
        activeFiltersInTemplate.assistedBy.forEach((stf) => {
          if (this.options.assistedBy.find((o) => o.code == stf.code)) {
            reconciledAssistedBy = [...reconciledAssistedBy, stf];
          }
        });
        if (reconciledAssistedBy.length == this.options.assistedBy.length) {
          this.currentTemplate.allAssistedBy = true;
          activeFiltersInTemplate.assistedBy = [];
        } else {
          activeFiltersInTemplate.assistedBy = reconciledAssistedBy;
        }
        break;
      case TypesForReconcile.Supervisors:
        let reconciledSupervisors = new Array<SupervisorFilterOption>();
        activeFiltersInTemplate.supervisors.forEach((stf) => {
          let supervisor = this.options.supervisors.find(
            (o) => o.code == stf.code
          );
          if (supervisor) {
            stf.localizedStaffFullname = supervisor.localizedStaffFullname;
            reconciledSupervisors = [...reconciledSupervisors, stf];
          }
        });
        if (reconciledSupervisors.length == this.options.supervisors.length) {
          this.currentTemplate.allSupervisors = true;
          activeFiltersInTemplate.supervisors = [];
        } else {
          activeFiltersInTemplate.supervisors = reconciledSupervisors;
        }
        break;
      case TypesForReconcile.Categories:
        let reconciledCategories = new Array<CategoryFilterOption>();
        activeFiltersInTemplate.categories.forEach((cat) => {
          if (this.options.categories.find((o) => o.code == cat.code)) {
            reconciledCategories = [...reconciledCategories, cat];
          }
        });
        if (reconciledCategories.length == this.options.categories.length) {
          this.currentTemplate.allCategories = true;
          activeFiltersInTemplate.categories = [];
        } else {
          activeFiltersInTemplate.categories = reconciledCategories;
        }
        break;
      case TypesForReconcile.Services:
        let reconciledServices = new Array<ServiceFilterOption>();
        activeFiltersInTemplate.services.forEach((svc) => {
          if (this.options.services.find((o) => o.code == svc.code)) {
            reconciledServices = [...reconciledServices, svc];
          }
        });
        if (reconciledServices.length == this.options.services.length) {
          this.currentTemplate.allServices = true;
          activeFiltersInTemplate.services = [];
        } else {
          activeFiltersInTemplate.services = reconciledServices;
        }
        break;
      case TypesForReconcile.Statuses:
        let reconciledStatuses = new Array<StatusFilterOption>();
        activeFiltersInTemplate.statuses.forEach((stat) => {
          let status = this.options.statuses.find(
            (o) => o.lobbyStatusId == stat.lobbyStatusId
          );
          if (status) {
            stat.localizedStatusName = status.localizedStatusName;
            reconciledStatuses = [...reconciledStatuses, stat];
          }
        });
        if (reconciledStatuses.length == this.options.statuses.length) {
          this.currentTemplate.allStatuses = true;
          activeFiltersInTemplate.statuses = [];
        } else {
          activeFiltersInTemplate.statuses = reconciledStatuses;
        }
        break;
      case TypesForReconcile.AssistTypes:
        let reconciledAssistTypes = new Array<AssistTypeFilterOption>();
        activeFiltersInTemplate.assistTypes.forEach((at) => {
          let assistType = this.options.assistTypes.find(
            (o) => o.assistTypeId == at.assistTypeId
          );
          if (assistType) {
            at.localizedAssistTypeName = assistType.localizedAssistTypeName;
            reconciledAssistTypes = [...reconciledAssistTypes, at];
          }
        });
        if (reconciledAssistTypes.length == this.options.assistTypes.length) {
          this.currentTemplate.allAssistTypes = true;
          activeFiltersInTemplate.assistTypes = [];
        } else {
          activeFiltersInTemplate.assistTypes = reconciledAssistTypes;
        }
        break;
      case TypesForReconcile.Views:
        let reconciledViews = new Array<ViewFilterOption>();
        activeFiltersInTemplate.defaultViews.forEach((v) => {
          if (this.options.defaultViews.find((o) => o.viewId == v.viewId)) {
            reconciledViews = [...reconciledViews, v];
          }
        });
        activeFiltersInTemplate.defaultViews = reconciledViews;
        break;
      case TypesForReconcile.AutoWrapUpStatuses:
        var reconciledAwStatuses = new Array<AutoWrapUpStatusFilterOption>();
        activeFiltersInTemplate.autoWrapUpStatus.forEach((v) => {
          if (
            this.options.autoWrapUpStatus.find(
              (o) => o.autoWrapUpStatusId == v.autoWrapUpStatusId
            )
          ) {
            reconciledAwStatuses = [...reconciledAwStatuses, v];
          }
        });
        activeFiltersInTemplate.autoWrapUpStatus = reconciledAwStatuses;
        break;
      case TypesForReconcile.Users:
        let reconciledUsers = new Array<UserFilterOption>();
        activeFiltersInTemplate.users.forEach((stf) => {
          if (this.options.users.find((o) => o.code == stf.code)) {
            reconciledUsers = [...reconciledUsers, stf];
          }
        });
        if (reconciledUsers.length == this.options.users.length) {
          this.currentTemplate.allUsers = true;
          activeFiltersInTemplate.users = [];
        } else {
          activeFiltersInTemplate.users = reconciledUsers;
        }
        break;
    }
  }

  // #region Handle Filter Template

  /* AddTemplate
   *  Adds a new template object to the templates collection object
   */
  public AddTemplate(newTemplate: FilterTemplateResponseModel) {
    this.templates.push(newTemplate);
    this.originalTemplates.push(new FilterTemplateResponseModel(newTemplate));
    this.SortTemplate(this.templates);
    this.SortTemplate(this.originalTemplates);
  }
  /* SortTemplate
   *  Sort the templates in Alphabetical order except None option in templates collection object
   */
  public SortTemplate(template: FilterTemplateResponseModel[]) {
    template.sort(function (a, b) {
      if (a.templateName.toLowerCase() == "none") {
        return -1;
      } else if (b.templateName.toLowerCase() == "none") {
        return +1;
      } else if (a.templateName.toLowerCase() > b.templateName.toLowerCase()) {
        return 1;
      } else if (a.templateName.toLowerCase() < b.templateName.toLowerCase()) {
        return -1;
      }
      return 0;
    });
  }
  /* RemoveTemplate
   *  Removes specified template object from the templates collection object if it exists
   */
  public RemoveTemplate(templateName: string) {
    let idx = this.templates.findIndex(
      (item) => item.templateName === templateName
    );
    if (idx > -1) {
      this.templates.splice(idx, 1);
    }
    idx = this.originalTemplates.findIndex(
      (item) => item.templateName === templateName
    );
    if (idx > -1) {
      this.originalTemplates.splice(idx, 1);
    }
    this.SortTemplate(this.templates);
    this.SortTemplate(this.originalTemplates);
  }
  /* GetTemplateByTemplateName
   *  Given a templateName to search for, the templates collection object is searched and, if a template
   *  of the name specified is found, that template is returned
   */
  public GetTemplateByTemplateName(
    templateName: string
  ): FilterTemplateResponseModel {
    var result: FilterTemplateResponseModel = new FilterTemplateResponseModel();
    for (var template of this.templates) {
      if (template.templateName == templateName) {
        result = template;
        break;
      }
    }
    return result;
  }
  /* ReplaceTemplateWith
   *  Searches for oldTemplateName in templates collection object and, if found, replaces that template with the template
   *  passed as the newTemplate parameter
   */
  public ReplaceTemplateWith(
    oldTemplateName: string,
    newTemplate: FilterTemplateResponseModel,
    target: FilterTemplateResponseModel[]
  ) {
    const idx = target.findIndex((item) => item.templateName === oldTemplateName);
    const cloned = JSON.parse(JSON.stringify(newTemplate));
    if (idx > -1) {
      target[idx] = cloned;
    } else {
      target.push(cloned);
    }
  }
  /* SetActiveTemplate
   *  Searches the templates collection object for templateName, and, if found, copies that template into the currentTemplate
   *  object. The 'currenTemplate' is the list of filters currently effective that shold be used for all queries by the grid
   *  and chart systems
   */
  public SetActiveTemplate(templateName: string) {
    for (const template of this.templates) {
      if (template.templateName == templateName) {
        this.currentTemplate = template;
        break;
      }
    }
  }
  // Search in templates array for the first (should be the only) template showing isDefault == true. Return the template's name
  public GetDefaultTemplate(data: FilterTemplateResponseModel[]) {
    let result: FilterTemplateResponseModel | undefined = data.find(
      (x) => x.templateId > 0 && x.isDefault === true
    );
    if (!result) {
      result = new FilterTemplateResponseModel();
      return data.find((x) => x.templateId < 0);
    }
    return result;
  }
  public GetResetDefaultTemplate(
    data: FilterTemplateResponseModel[],
    templatedId: number
  ) {
    var result: FilterTemplateResponseModel | undefined;
    result = data.find(
      (x) =>
        x.templateId > 0 && (x.templateId == templatedId || x.isDefault == true)
    );
    if (!result) {
      result = data.find((x) => x.templateId < 0);
    }
    return result;
  }

  public AdjustDefaults(
    template: FilterTemplateResponseModel,
    target: FilterTemplateResponseModel[]
  ): FilterTemplateResponseModel {
    var result: FilterTemplateResponseModel = new FilterTemplateResponseModel();
    if (template.isDefault) {
      for (let existingTemplate of target) {
        if (existingTemplate.templateName != template.templateName) {
          if (existingTemplate.isDefault) {
            existingTemplate.isDefault = false;
            result = existingTemplate;
            break;
          }
        }
      }
    }
    return result; // returns template that was UNSET as default if any
  }

  /* TemplateExists
   *  checks the templates object for the presence of a template bearing the name passed into the templateName parameter
   *  returns true/false if specified template is/is not found
   */
  public TemplateExists(templateName: string): boolean {
    let result = false;
    for (const template of this.templates) {
      if (template.templateName.toLowerCase() == templateName.toLowerCase()) {
        result = true;
        break;
      }
    }
    return result;
  }
  public SaveTemplateExists(templateName: string, templateId: number): boolean {
    var result = false;
    for (const template of this.templates) {
      if (
        template.templateName.toLowerCase() == templateName.toLowerCase() &&
        template.templateId != templateId
      ) {
        result = true;
        break;
      }
    }
    return result;
  }
  public GetOrgTemplateName(templateId: number): string {
    var result = false;
    for (const template of this.templates) {
      if (template.templateId == templateId) {
        return template.templateName;
        break;
      }
    }
    return "";
  }

  //#endregion Handle Filter Template

  // #region Compares for equivalency of two submitted templates

  /* SelectedAssistTypesMatch
   *  Compares for equivalency of the AssistTypes array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedAssistTypesMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.assistTypes.length !=
      second.activeFilters.assistTypes.length
    ) {
      return false;
    } else if (first.activeFilters.assistTypes.length == 0) {
      return true;
    } else {
      first.activeFilters.assistTypes.forEach((f) => {
        result = false;
        for (const assistType of second.activeFilters.assistTypes) {
          if (assistType.assistTypeId == f.assistTypeId) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedAssistedBysMatch
   *  Compares for equivalency of the AssistedBy array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedAssistedBysMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.assistedBy.length !=
      second.activeFilters.assistedBy.length
    ) {
      return false;
    } else if (first.activeFilters.assistedBy.length == 0) {
      return true;
    } else {
      first.activeFilters.assistedBy.forEach((f) => {
        result = false;
        for (const element of second.activeFilters.assistedBy) {
          if (element.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedCategoriesMatch
   *  Compares for equivalency of the Categories array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedCategoriesMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.categories.length !=
      second.activeFilters.categories.length
    ) {
      return false;
    } else if (first.activeFilters.categories.length == 0) {
      return true;
    } else {
      first.activeFilters.categories.forEach((f) => {
        result = false;
        for (const category of second.activeFilters.categories) {
          if (category.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedDaysMatch
   *  Compares for equivalency of the DaysOfWeek array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedDaysMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.daysOfWeek.length !=
      second.activeFilters.daysOfWeek.length
    ) {
      return false;
    } else if (first.activeFilters.daysOfWeek.length == 0) {
      return true;
    } else {
      first.activeFilters.daysOfWeek.forEach((f) => {
        result = false;
        for (const element of second.activeFilters.daysOfWeek) {
          if (element.dayOfWeek == f.dayOfWeek) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedLocationsMatch
   * Compares for equivalency of the Locations array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedLocationsMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.locations.length !=
      second.activeFilters.locations.length
    ) {
      return false;
    } else if (first.activeFilters.locations.length == 0) {
      return true;
    } else {
      first.activeFilters.locations.forEach((f) => {
        result = false;
        for (const location of second.activeFilters.locations) {
          if (location.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedRequestedStaffMatch
   *  Compares for equivalency of the RequestedStaff array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedRequestedStaffMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.requestedStaff.length !=
      second.activeFilters.requestedStaff.length
    ) {
      return false;
    } else if (first.activeFilters.requestedStaff.length == 0) {
      return true;
    } else {
      first.activeFilters.requestedStaff.forEach((f) => {
        result = false;
        for (const element of second.activeFilters.requestedStaff) {
          if (element.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedServicesMatch
   * Compares for equivalency of the Services array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedServicesMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.services.length !=
      second.activeFilters.services.length
    ) {
      return false;
    } else if (first.activeFilters.services.length == 0) {
      return true;
    } else {
      first.activeFilters.services.forEach((f) => {
        result = false;
        for (const service of second.activeFilters.services) {
          if (service.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedStatusesMatch
   * Compares for equivalency of the Statuses array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedStatusesMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.statuses.length !=
      second.activeFilters.statuses.length
    ) {
      return false;
    } else if (first.activeFilters.statuses.length == 0) {
      return true;
    } else {
      first.activeFilters.statuses.forEach((f) => {
        result = false;
        for (const status of second.activeFilters.statuses) {
          if (status.lobbyStatusId == f.lobbyStatusId) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedSupervisorsMatch
   * Compares for equivalency of the Supervisors array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedSupervisorsMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.supervisors.length !=
      second.activeFilters.supervisors.length
    ) {
      return false;
    } else if (first.activeFilters.supervisors.length == 0) {
      return true;
    } else {
      first.activeFilters.supervisors.forEach((f) => {
        result = false;
        for (const supervisor of second.activeFilters.supervisors) {
          if (supervisor.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedTimePeriodsMatch
   * Compares for equivalency of the TimePeriods array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedTimePeriodsMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.timePeriods.length !=
      second.activeFilters.timePeriods.length
    ) {
      return false;
    } else if (first.activeFilters.timePeriods.length == 0) {
      return true;
    } else {
      first.activeFilters.timePeriods.forEach((f) => {
        result = false;
        for (const timePeriod of second.activeFilters.timePeriods) {
          if (timePeriod.detailViewTimePeriodId == f.detailViewTimePeriodId) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedViewsMatch
   *  Compares for equivalency of the DefaultViews array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedViewsMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.defaultViews == undefined ||
      first.activeFilters.defaultViews.length == 0
    ) {
      result = true;
    } else if (
      first.activeFilters.defaultViews.length !=
      second.activeFilters.defaultViews.length
    ) {
      result = false;
    } else {
      first.activeFilters.defaultViews.forEach((f) => {
        result = false;
        for (var i = 0; i < second.activeFilters.defaultViews.length; i++) {
          if (second.activeFilters.defaultViews[i].viewId == f.viewId) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedAWStatusMatch
   *  Compares for equivalency of the AutoWrapUpStatus array inside the two submitted templates
   * returns true/false based on the equivalency found
   */
  public SelectedAWStatusMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    // if (first.activeFilters.autoWrapUpStatus == undefined || first.activeFilters.autoWrapUpStatus.length == 0) {
    //   result = true;
    // }
    // else
    if (
      first.activeFilters.autoWrapUpStatus.length !=
      second.activeFilters.autoWrapUpStatus.length
    ) {
      result = false;
    } else {
      first.activeFilters.autoWrapUpStatus.forEach((f) => {
        result = false;
        for (var i = 0; i < second.activeFilters.autoWrapUpStatus.length; i++) {
          if (
            second.activeFilters.autoWrapUpStatus[i].autoWrapUpStatusId ==
            f.autoWrapUpStatusId
          ) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }
  /* SelectedSearchTermsMatch
   *  Compares for equivalency of the SearchTerms array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedSearchTermsMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (
      first.activeFilters.searchTerms.length !=
      second.activeFilters.searchTerms.length
    ) {
      result = false;
    } else {
      first.activeFilters.searchTerms.forEach((f) => {
        result = false;
        for (var i = 0; i < second.activeFilters.searchTerms.length; i++) {
          if (
            second.activeFilters.searchTerms[i].searchTerm.trim() ==
            f.searchTerm.trim()
          ) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }

  /* SelectedUsersMatch
   *  Compares for equivalency of the Users array inside the two submitted templates
   *  returns true/false based on the equivalency found
   */
  public SelectedUsersMatch(
    first: FilterTemplateResponseModel,
    second: FilterTemplateResponseModel
  ): boolean {
    let result = false;
    if (first.activeFilters.users.length != second.activeFilters.users.length) {
      return false;
    } else if (first.activeFilters.users.length == 0) {
      return true;
    } else {
      first.activeFilters.users.forEach((f) => {
        result = false;
        for (const element of second.activeFilters.users) {
          if (element.code == f.code) {
            result = true;
            break;
          }
        }
      });
    }
    return result;
  }

  //#endregion

  // #region Template to Options / Options to template mappers

  // ******************************************************************
  // Template to Options / Options to template mappers
  // Each of these functions operates similarly as follows:
  // When direction = TemplateToOptions, handles conversion between the object in a given template (designated per the function name) to the related
  // array in the options object - which is used in the FileterOptions page to manage the dropdowns and their selections. The
  // incoming template is reconciled (see ReconcileTemplateWithOptions description earlier) before the conversion is performed.
  // When direction = OptionsToTemplate, maps the options.selectedXXX array back onto the template passed in param 'result'
  // That result (i.e. the modified template object) is then returned to the caller
  // ******************************************************************

  // MapLocations
  public MapLocations(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }

    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedLocations) {
        this.options.selectedLocations = new Array<LocationFilterOption>();
      }
      if (this.currentTemplate.allLocations == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Locations);
        if (this.currentTemplate.allLocations == false) {
          this.options.selectedLocations = [];
          this.currentTemplate.activeFilters.locations.forEach((l) => {
            this.options.selectedLocations = [
              ...this.options.selectedLocations,
              {
                locationId: l.locationId,
                code: l.code,
                locationName: l.locationName,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allLocations == true) {
        const source =
          this.currentTemplate.activeFilters?.locations &&
          this.currentTemplate.activeFilters.locations.length > 0
            ? this.currentTemplate.activeFilters.locations
            : this.options.locations;
        this.options.selectedLocations = source.map((loc) => ({
          locationId: loc.locationId,
          code: loc.code,
          locationName: loc.locationName,
          timeZone: (loc as any).timeZone,
        }));
      }
    } else {
      result.allLocations = true;
      this.options.locations.forEach((l) => {
        const code = l.code;
        const locationId = l.locationId;
        let codeFound = false;
        this.options.selectedLocations.forEach((sl) => {
          const selectedCode = (sl as any).code;
          const selectedLocationId = (sl as any).locationId;
          if (
            (selectedCode != null && selectedCode == code) ||
            (selectedLocationId != null && selectedLocationId == locationId)
          ) {
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.locations) {
              result!.activeFilters.locations = [];
            }
            sl.locationId = locationId;
            result!.activeFilters.locations = [
              ...result!.activeFilters.locations,
              sl,
            ];
          }
        });
        if (!codeFound) {
          result!.allLocations = false;
        }
      });
      if (
        !result.allLocations &&
        (!result.activeFilters ||
          !result.activeFilters.locations ||
          result.activeFilters.locations.length === 0) &&
        this.options.selectedLocations &&
        this.options.selectedLocations.length > 0
      ) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.locations = (this.options.selectedLocations || []).map((sl: any) => {
          const matched = (this.options.locations || []).find(
            (l: any) =>
              (sl?.code != null && l?.code == sl.code) ||
              (sl?.locationId != null && l?.locationId == sl.locationId)
          );
          return {
            locationId: matched?.locationId ?? sl.locationId,
            code: matched?.code ?? sl.code,
            locationName: matched?.locationName ?? sl.locationName,
          };
        });
      }
      if (result.allLocations && (!result.activeFilters || !result.activeFilters.locations || result.activeFilters.locations.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.locations = [...(this.options.selectedLocations || this.options.locations || [])];
      }
    }
    return result;
  }
  // MapTimePeriods
  public MapTimePeriods(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      //if (!this.options.selectedTimePeriod) {
      this.options.selectedTimePeriod = new Array<TimePeriodFilterOption>();
      //}
      this.currentTemplate.activeFilters.timePeriods.forEach((tp) => {
        this.options.selectedTimePeriod = [
          ...this.options.selectedTimePeriod,
          tp,
        ];
      });
    } else {
      if (this.useTimePeriod == true) {
        this.options.selectedTimePeriod.forEach((tp) => {
          const name = tp.timePeriodName;
          let nameFound = false;
          this.options.selectedTimePeriod.forEach((stp) => {
            if (stp.timePeriodName == name) {
              nameFound = true;
              if (!result!.activeFilters) {
                result!.activeFilters = new FiltersOptionsModel();
              }
              if (!result!.activeFilters.timePeriods) {
                result!.activeFilters.timePeriods = [];
              }
              result!.activeFilters.timePeriods = [
                ...result!.activeFilters.timePeriods,
                stp,
              ];
            }
          });
        });
      } else {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        if (!result.activeFilters.timePeriods) {
          result.activeFilters.timePeriods = [];
        }
        // just duplicate the original
        result.activeFilters.timePeriods =
          this.currentTemplate.activeFilters.timePeriods.map((x) => x);
      }
    }
    return result;
  }
  // MapDaysOfWeek
  public MapDaysOfWeek(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedDaysOfWeek) {
        this.options.selectedDaysOfWeek = new Array<DayOfWeekFilterOption>();
      }
      if (this.currentTemplate.allDays == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.DaysOfWeek);
        if (this.currentTemplate.allDays == false) {
          this.options.selectedDaysOfWeek = [];
          this.currentTemplate.activeFilters.daysOfWeek.forEach((dow) => {
            this.options.selectedDaysOfWeek = [
              ...this.options.selectedDaysOfWeek,
              {
                dayOfWeek: dow.dayOfWeek,
                localizedDayOfWeekName: dow.localizedDayOfWeekName,
                dayOfWeekName: dow.dayOfWeekName as string,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allDays == true) {
        const source =
          this.currentTemplate.activeFilters?.daysOfWeek &&
          this.currentTemplate.activeFilters.daysOfWeek.length > 0
            ? this.currentTemplate.activeFilters.daysOfWeek
            : this.options.daysOfWeek;
        this.options.selectedDaysOfWeek = source.map((dow) => ({
          dayOfWeek: dow.dayOfWeek,
          dayOfWeekName: dow.dayOfWeekName,
          localizedDayOfWeekName: dow.localizedDayOfWeekName,
        }));
      }
    } else {
      result.allDays = true;
      this.options.daysOfWeek.forEach((dow) => {
        const day = dow.dayOfWeek;
        let dayFound = false;
        this.options.selectedDaysOfWeek.forEach((d) => {
          if (d.dayOfWeek == day) {
            d.dayOfWeekName = dow.dayOfWeekName;
            dayFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.daysOfWeek) {
              result!.activeFilters.daysOfWeek = [];
            }
            result!.activeFilters.daysOfWeek = [
              ...result!.activeFilters.daysOfWeek,
              d,
            ];
          }
        });
        if (!dayFound) {
          result!.allDays = false;
        }
      });
      if (result.allDays && (!result.activeFilters || !result.activeFilters.daysOfWeek || result.activeFilters.daysOfWeek.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.daysOfWeek = [...(this.options.selectedDaysOfWeek || this.options.daysOfWeek || [])];
      }
    }
    return result;
  }
  // MapRequestedStaff
  public MapRequestedStaff(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedRequestedStaff) {
        this.options.selectedRequestedStaff =
          new Array<RequestedStaffFilterOption>();
      }
      if (this.currentTemplate.allRequestedStaff == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.RequestedStaff);
        if (this.currentTemplate.allRequestedStaff == false) {
          this.options.selectedRequestedStaff = [];
          this.currentTemplate.activeFilters.requestedStaff.forEach((stf) => {
            this.options.selectedRequestedStaff = [
              ...this.options.selectedRequestedStaff,
              {
                staffId: stf.staffId,
                code: stf.code,
                staffFullname: stf.staffFullname,
                localizedStaffFullname: stf.localizedStaffFullname,
                isNotificationGroup: stf.isNotificationGroup,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allRequestedStaff == true) {
        const hasActive =
          this.currentTemplate.activeFilters?.requestedStaff &&
          this.currentTemplate.activeFilters.requestedStaff.length > 0;
        const source = hasActive
          ? this.currentTemplate.activeFilters.requestedStaff
          : this.options.requestedStaff;

        this.options.selectedRequestedStaff = source.map((stf) => ({
          staffId: stf.staffId,
          code: stf.code,
          staffFullname: stf.staffFullname,
          localizedStaffFullname: stf.localizedStaffFullname,
          isNotificationGroup: stf.isNotificationGroup,
          staffSupervisorsId: (stf as any).staffSupervisorsId,
          supervisorCode: (stf as any).supervisorCode,
          isSupervisor: (stf as any).isSupervisor,
        }));
      }
    } else {
      result.allRequestedStaff = true;
      this.options.requestedStaff.forEach((s) => {
        const code = s.code;
        const staffId = s.staffId;
        let codeFound = false;
        this.options.selectedRequestedStaff.forEach((ss) => {
          if (ss.code == code) {
            ss.staffFullname = s.staffFullname;
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.requestedStaff) {
              result!.activeFilters.requestedStaff = [];
            }
            ss.staffId = staffId;
            ss.isNotificationGroup = s.isNotificationGroup;
            ss.code = s.code;
            ss.localizedStaffFullname = s.localizedStaffFullname;
            result!.activeFilters.requestedStaff = [
              ...result!.activeFilters.requestedStaff,
              ss,
            ];
          }
        });
        if (!codeFound) {
          result!.allRequestedStaff = false;
        }
      });
      if (result.allRequestedStaff && (!result.activeFilters || !result.activeFilters.requestedStaff || result.activeFilters.requestedStaff.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.requestedStaff = [...(this.options.selectedRequestedStaff || this.options.requestedStaff || [])];
      }
    }
    return result;
  }
  // MapAssistedBys
  public MapAssistedBys(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedAssistedBys) {
        this.options.selectedAssistedBys = new Array<AssistedByFilterOption>();
      }
      if (this.currentTemplate.allAssistedBy == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.AssistedBys);
        if (this.currentTemplate.allAssistedBy == false) {
          this.options.selectedAssistedBys = [];
          this.currentTemplate.activeFilters.assistedBy.forEach((stf) => {
            this.options.selectedAssistedBys = [
              ...this.options.selectedAssistedBys,
              {
                staffId: stf.staffId,
                code: stf.code,
                staffFullname: stf.staffFullname,
                staffSupervisorsId: stf.staffSupervisorsId,
                supervisorCode: stf.supervisorCode,
                isSupervisor: stf.isSupervisor,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allAssistedBy == true) {
        const source =
          this.currentTemplate.activeFilters?.assistedBy &&
          this.currentTemplate.activeFilters.assistedBy.length > 0
            ? this.currentTemplate.activeFilters.assistedBy
            : this.options.filteredAssistedBys || this.options.assistedBy;
        this.options.selectedAssistedBys = source.map((stf) => ({
          staffId: stf.staffId,
          code: stf.code,
          staffFullname: stf.staffFullname,
          staffSupervisorsId: stf.staffSupervisorsId,
          supervisorCode: stf.supervisorCode,
          isSupervisor: stf.isSupervisor,
          isNotificationGroup: stf.isNotificationGroup,
        }));
      }
    } else {
      result.allAssistedBy = true;
      this.options.assistedBy.forEach((s) => {
        const code = s.code;
        const staffId = s.staffId;
        let codeFound = false;
        this.options.selectedAssistedBys.forEach((ss) => {
          if (ss.code == code) {
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.assistedBy) {
              result!.activeFilters.assistedBy = [];
            }
            ss.staffId = staffId;
            result!.activeFilters.assistedBy = [
              ...result!.activeFilters.assistedBy,
              ss,
            ];
          }
        });
        if (!codeFound) {
          result!.allAssistedBy = false;
        }
      });
      if (result.allAssistedBy && (!result.activeFilters || !result.activeFilters.assistedBy || result.activeFilters.assistedBy.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.assistedBy = [...(this.options.selectedAssistedBys || this.options.assistedBy || [])];
      }
    }
    return result;
  }
  // MapSupervisors
  public MapSupervisors(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedSupervisors) {
        this.options.selectedSupervisors = new Array<SupervisorFilterOption>();
      }
      if (this.currentTemplate.allSupervisors == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Supervisors);
        if (this.currentTemplate.allSupervisors == false) {
          this.options.selectedSupervisors = [];
          this.currentTemplate.activeFilters.supervisors.forEach((stf) => {
            this.options.selectedSupervisors = [
              ...this.options.selectedSupervisors,
              {
                staffId: stf.staffId,
                code: stf.code,
                staffFullname: stf.staffFullname,
                localizedStaffFullname: stf.localizedStaffFullname,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allSupervisors == true) {
        const source =
          this.currentTemplate.activeFilters?.supervisors &&
          this.currentTemplate.activeFilters.supervisors.length > 0
            ? this.currentTemplate.activeFilters.supervisors
            : this.options.supervisors;
        this.options.selectedSupervisors = source.map((stf) => ({
          staffId: stf.staffId,
          code: stf.code,
          staffFullname: stf.staffFullname,
          localizedStaffFullname: stf.localizedStaffFullname,
        }));
      }
    } else {
      result.allSupervisors = true;
      this.options.supervisors.forEach((s) => {
        const code = s.code;
        const id = s.staffId;
        let codeFound = false;
        this.options.selectedSupervisors.forEach((ss) => {
          if (ss.code == code) {
            ss.staffFullname = s.staffFullname;
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.supervisors) {
              result!.activeFilters.supervisors = [];
            }
            ss.staffId = id;
            result!.activeFilters.supervisors = [
              ...result!.activeFilters.supervisors,
              ss,
            ];
          }
        });
        if (!codeFound && result) {
          result.allSupervisors = false;
        }
      });
      if (result.allSupervisors && (!result.activeFilters || !result.activeFilters.supervisors || result.activeFilters.supervisors.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.supervisors = [...(this.options.selectedSupervisors || this.options.supervisors || [])];
      }
    }
    return result;
  }
  // MapCategories
  public MapCategories(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedCategories) {
        this.options.selectedCategories = new Array<CategoryFilterOption>();
      }
      if (this.currentTemplate.allCategories == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Categories);
        if (this.currentTemplate.allCategories == false) {
          this.options.selectedCategories = [];
          this.currentTemplate.activeFilters.categories.forEach((cat) => {
            this.options.selectedCategories = [
              ...this.options.selectedCategories,
              {
                categoryId: cat.categoryId,
                code: cat.code,
                categoryName: cat.categoryName,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allCategories == true) {
        const source =
          this.currentTemplate.activeFilters?.categories &&
          this.currentTemplate.activeFilters.categories.length > 0
            ? this.currentTemplate.activeFilters.categories
            : this.options.categories;
        this.options.selectedCategories = source.map((cat) => ({
          categoryId: cat.categoryId,
          code: cat.code,
          categoryName: cat.categoryName,
        }));
      }
    } else {
      result.allCategories = true;
      this.options.categories.forEach((c) => {
        const code = c.code;
        const categoryId = c.categoryId;
        let codeFound = false;
        this.options.selectedCategories.forEach((sc) => {
          if (sc.code == code) {
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.categories) {
              result!.activeFilters.categories = [];
            }
            sc.categoryId = categoryId;
            result!.activeFilters.categories = [
              ...result!.activeFilters.categories,
              sc,
            ];
          }
        });
        if (!codeFound) {
          result!.allCategories = false;
        }
      });
      if (result.allCategories && (!result.activeFilters || !result.activeFilters.categories || result.activeFilters.categories.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.categories = [...(this.options.selectedCategories || this.options.categories || [])];
      }
    }
    return result;
  }
  // MapServices
  public MapServices(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedServices) {
        this.options.selectedServices = new Array<ServiceFilterOption>();
      }
      if (this.currentTemplate.allServices == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Services);
        if (this.currentTemplate.allServices == false) {
          this.options.selectedServices = [];
          this.currentTemplate.activeFilters.services.forEach((svc) => {
            this.options.selectedServices = [
              ...this.options.selectedServices,
              {
                serviceId: svc.serviceId,
                code: svc.code,
                serviceName: svc.serviceName,
                categoryId: svc.categoryId,
                categoryName: svc.categoryName,
                categoryCode: svc.categoryCode,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allServices == true) {
        const source =
          this.currentTemplate.activeFilters?.services &&
          this.currentTemplate.activeFilters.services.length > 0
            ? this.currentTemplate.activeFilters.services
            : this.options.filteredServices || this.options.services;
        this.options.selectedServices = source.map((svc) => ({
          serviceId: svc.serviceId,
          code: svc.code,
          serviceName: svc.serviceName,
          categoryId: svc.categoryId,
          categoryName: svc.categoryName,
          categoryCode: svc.categoryCode,
        }));
      }
    } else {
      result.allServices = true;

      this.options.services.forEach((s) => {
        const code = s.code;
        const serviceId = s.serviceId;
        let codeFound = false;
        this.options.selectedServices.forEach((ss) => {
          if (ss.code == code) {
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.services) {
              result!.activeFilters.services = [];
            }
            ss.serviceId = serviceId;
            result!.activeFilters.services = [
              ...result!.activeFilters.services,
              ss,
            ];
          }
        });
        if (!codeFound) {
          result!.allServices = false;
        }
      });
      if (result.allServices && (!result.activeFilters || !result.activeFilters.services || result.activeFilters.services.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.services = [...(this.options.selectedServices || this.options.services || [])];
      }
    }
    return result;
  }
  // MapStatuses
  public MapStatuses(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedStatuses) {
        this.options.selectedStatuses = new Array<StatusFilterOption>();
      }
      if (this.currentTemplate.allStatuses == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Statuses);
        if (this.currentTemplate.allStatuses == false) {
          this.options.selectedStatuses = [];
          this.currentTemplate.activeFilters.statuses.forEach((stat) => {
            this.options.selectedStatuses = [
              ...this.options.selectedStatuses,
              {
                lobbyStatusId: stat.lobbyStatusId,
                statusName: stat.statusName,
                localizedStatusName: stat.localizedStatusName,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allStatuses == true) {
        const source =
          this.currentTemplate.activeFilters?.statuses &&
          this.currentTemplate.activeFilters.statuses.length > 0
            ? this.currentTemplate.activeFilters.statuses
            : this.options.statuses;
        this.options.selectedStatuses = source.map((stat) => ({
          lobbyStatusId: stat.lobbyStatusId,
          statusName: stat.statusName,
          localizedStatusName: stat.localizedStatusName,
        }));
      }
    } else {
      result.allStatuses = true;
      this.options.statuses.forEach((s) => {
        const statusId = s.lobbyStatusId;
        let idFound = false;
        this.options.selectedStatuses.forEach((ss) => {
          if (ss.lobbyStatusId == statusId) {
            ss.statusName = s.statusName;
            idFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.statuses) {
              result!.activeFilters.statuses = [];
            }
            result!.activeFilters.statuses = [
              ...result!.activeFilters.statuses,
              ss,
            ];
          }
        });
        if (!idFound) {
          result!.allStatuses = false;
        }
      });
      if (result.allStatuses && (!result.activeFilters || !result.activeFilters.statuses || result.activeFilters.statuses.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.statuses = [...(this.options.selectedStatuses || this.options.statuses || [])];
      }
    }
    return result;
  }
  // MapAssistTypes
  public MapAssistTypes(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedAssistTypes) {
        this.options.selectedAssistTypes = new Array<AssistTypeFilterOption>();
      }
      if (this.currentTemplate.allAssistTypes == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.AssistTypes);
        if (this.currentTemplate.allAssistTypes == false) {
          this.options.selectedAssistTypes = [];
          this.currentTemplate.activeFilters.assistTypes.forEach((at) => {
            this.options.selectedAssistTypes = [
              ...this.options.selectedAssistTypes,
              {
                assistTypeId: at.assistTypeId,
                assistTypeName: at.assistTypeName,
                localizedAssistTypeName: at.localizedAssistTypeName,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allAssistTypes == true) {
        const source =
          this.currentTemplate.activeFilters?.assistTypes &&
          this.currentTemplate.activeFilters.assistTypes.length > 0
            ? this.currentTemplate.activeFilters.assistTypes
            : this.options.assistTypes;
        this.options.selectedAssistTypes = source.map((at) => ({
          assistTypeId: at.assistTypeId,
          assistTypeName: at.assistTypeName,
          localizedAssistTypeName: at.localizedAssistTypeName,
        }));
      }
    } else {
      result.allAssistTypes = true;
      this.options.assistTypes.forEach((a) => {
        const assistTypeId = a.assistTypeId;
        let idFound = false;
        this.options.selectedAssistTypes.forEach((sa) => {
          if (sa.assistTypeId == assistTypeId) {
            sa.assistTypeName = a.assistTypeName;
            idFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.assistTypes) {
              result!.activeFilters.assistTypes = [];
            }
            result!.activeFilters.assistTypes = [
              ...result!.activeFilters.assistTypes,
              sa,
            ];
          }
        });
        if (!idFound) {
          result!.allAssistTypes = false;
        }
      });
      if (result.allAssistTypes && (!result.activeFilters || !result.activeFilters.assistTypes || result.activeFilters.assistTypes.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.assistTypes = [...(this.options.selectedAssistTypes || this.options.assistTypes || [])];
      }
    }
    return result;
  }
  // MapViews
  public MapViews(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      //if (!this.options.selectedViews) {
      this.options.selectedViews = new Array<ViewFilterOption>();
      //}
      this.ReconcileTemplateWithOptions(TypesForReconcile.Views);
      this.currentTemplate.activeFilters.defaultViews.forEach((v) => {
        this.options.selectedViews = [
          ...this.options.selectedViews,
          {
            viewId: v.viewId,
            viewName: v.viewName,
            viewLocalizedName: this.translateService.instant(
              this.viewsLocalizedKeys[v.viewId]
            ),
          },
        ];
      });
    } else {
      this.options.defaultViews.forEach((v) => {
        const viewId = v.viewId;
        let idFound = false;
        this.options.selectedViews.forEach((sv) => {
          if (Number(sv.viewId) === Number(viewId)) {
            idFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.defaultViews) {
              result!.activeFilters.defaultViews = [];
            }
            result!.activeFilters.defaultViews = [
              ...result!.activeFilters.defaultViews,
              sv,
            ];
          }
        });
      });
    }
    return result;
  }
  // MapAutoWrapUpStatuses
  public MapAutoWrapUpStatuses(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      //if (!this.options.selectedAutoWrapUpStatus) {
      this.options.selectedAutoWrapUpStatus =
        new Array<AutoWrapUpStatusFilterOption>();
      //}

      this.ReconcileTemplateWithOptions(TypesForReconcile.AutoWrapUpStatuses);

      this.currentTemplate.activeFilters.autoWrapUpStatus.forEach((v) => {
        const id = Number(v.autoWrapUpStatusId);
        const displayText =
          id === -1
            ? this.translateService.instant("_Report_Filter_Show_All_Text_")
            : id === 0
              ? this.translateService.instant("_Report_Filter_Exclude_Text_")
              : id === 1
                ? this.translateService.instant(
                    "_Report_Filter_Show_Only_Text_"
                  )
                : v.autoWrapUpStatus;
        this.options.selectedAutoWrapUpStatus = [
          ...this.options.selectedAutoWrapUpStatus,
          {
            autoWrapUpStatusId: v.autoWrapUpStatusId,
            autoWrapUpStatus: displayText,
            localizedAutoWrapUpStatus: displayText,
          },
        ];
      });

      if (this.options.selectedAutoWrapUpStatus.length == 0) {
        this.options.selectedAutoWrapUpStatus = [];
        this.options.autoWrapUpStatus.forEach((stat) => {
          this.options.selectedAutoWrapUpStatus = [
            ...this.options.selectedAutoWrapUpStatus,
            stat,
          ];
        });
      }
    } else {
      this.options.autoWrapUpStatus.forEach((v) => {
        const autoWrapUpStatusId = v.autoWrapUpStatusId;
        let idFound = false;
        this.options.selectedAutoWrapUpStatus.forEach((sv) => {
          if (sv.autoWrapUpStatusId == autoWrapUpStatusId) {
            idFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.autoWrapUpStatus) {
              result!.activeFilters.autoWrapUpStatus = [];
            }
            result!.activeFilters.autoWrapUpStatus = [
              ...result!.activeFilters.autoWrapUpStatus,
              {
                autoWrapUpStatusId: sv.autoWrapUpStatusId,
                autoWrapUpStatus:
                  sv.autoWrapUpStatus ?? sv.localizedAutoWrapUpStatus ?? "",
              },
            ];
          }
        });
      });
    }
    return result;
  }

  // MapUsers
  public MapUsers(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      if (!this.options.selectedUsers) {
        this.options.selectedUsers = new Array<UserFilterOption>();
      }
      if (this.currentTemplate.allUsers == false) {
        this.ReconcileTemplateWithOptions(TypesForReconcile.Users);
        if (this.currentTemplate.allUsers == false) {
          this.options.selectedUsers = [];
          this.currentTemplate.activeFilters.users.forEach((stf) => {
            this.options.selectedUsers = [
              ...this.options.selectedUsers,
              {
                staffId: stf.staffId,
                code: stf.code,
                staffFullname: stf.staffFullname,
              },
            ];
          });
        }
      }
      if (this.currentTemplate.allUsers == true) {
        const source =
          this.currentTemplate.activeFilters?.users &&
          this.currentTemplate.activeFilters.users.length > 0
            ? this.currentTemplate.activeFilters.users
            : this.options.users;
        this.options.selectedUsers = source.map((stf) => ({
          staffId: stf.staffId,
          code: stf.code,
          staffFullname: (stf as any).staffFullname,
          staffFName: (stf as any).staffFName,
          staffLName: (stf as any).staffLName,
          staffSupervisorsId: (stf as any).staffSupervisorsId,
          supervisorCode: (stf as any).supervisorCode,
          isSupervisor: (stf as any).isSupervisor,
          isNotificationGroup: (stf as any).isNotificationGroup,
        }));
      }
    } else {
      result.allUsers = true;
      this.options.users.forEach((s) => {
        const code = s.code;
        const staffId = s.staffId;
        let codeFound = false;
        this.options.selectedUsers.forEach((ss) => {
          if (ss.code == code) {
            codeFound = true;
            if (!result!.activeFilters) {
              result!.activeFilters = new FiltersOptionsModel();
            }
            if (!result!.activeFilters.users) {
              result!.activeFilters.users = [];
            }
            ss.staffId = staffId;
            result!.activeFilters.users = [...result!.activeFilters.users, ss];
          }
        });
        if (!codeFound) {
          result!.allUsers = false;
        }
      });
      if (result.allUsers && (!result.activeFilters || !result.activeFilters.users || result.activeFilters.users.length === 0)) {
        if (!result.activeFilters) {
          result.activeFilters = new FiltersOptionsModel();
        }
        result.activeFilters.users = [...(this.options.selectedUsers || this.options.users || [])];
      }
    }
    return result;
  }
  // MapSearchTerms
  //
  // Note: this function does NOT run the template through the reconcilation
  public MapSearchTerms(
    direction: MappingDirection,
    result?: FilterTemplateResponseModel
  ) {
    if (!result) {
      result = new FilterTemplateResponseModel();
    }
    if (direction == MappingDirection.TemplateToOptions) {
      this.options.searchTerms = new Array<SearchTermsFilterOption>();
      if (
        this.currentTemplate.activeFilters.searchTerms &&
        this.currentTemplate.activeFilters.searchTerms.length > 0
      ) {
        this.currentTemplate.activeFilters.searchTerms.forEach((t) => {
          this.options.searchTerms = [
            ...this.options.searchTerms,
            {
              detailViewSearchTermId: t.detailViewSearchTermId,
              searchTerm: t.searchTerm,
            },
          ];
        });
      } else {
        this.options.searchTerms = [
          ...this.options.searchTerms,
          { detailViewSearchTermId: -1, searchTerm: "" },
        ];
      }
    } else {
      if (!result.activeFilters) {
        result.activeFilters = new FiltersOptionsModel();
      }
      if (!result.activeFilters.searchTerms) {
        result.activeFilters.searchTerms = new Array<SearchTermsFilterOption>();
      }
      result.activeFilters.searchTerms.length = 0;
      if (this.options.searchTerms && this.options.searchTerms.length > 0) {
        result.activeFilters.searchTerms.push(this.options.searchTerms[0]);
      }
    }
    return result;
  }

  //#endregion Template to Options / Options to template mappers

  public filterServicesByCategory(item?: any) {
    if (!this.options.services) {
      return;
    }

    const previousSelectedServices = this.options.selectedServices
      ? [...this.options.selectedServices]
      : [];
    const previousFilteredCategoryIds = new Set<number>(
      (this.options.filteredServices || [])
        .map((s) => Number(s.categoryId))
        .filter((id) => !isNaN(id))
    );
    const previousFilteredCategoryCodes = new Set<string>(
      (this.options.filteredServices || [])
        .map((s) => (s.categoryCode || "").toString())
        .filter((code) => !!code)
    );

    if (!this.options.filteredServices) {
      this.options.filteredServices = new Array<ServiceFilterOption>();
    }

    const selectedCategoryInputs: CategoryFilterOption[] = Array.isArray(item)
      ? item
      : this.options.selectedCategories || [];

    if (selectedCategoryInputs.length === 0) {
      this.options.filteredServices = [...this.options.services];
      const preservedSelections = this.options.services.filter((svc) =>
        previousSelectedServices.some((selected) => selected.code === svc.code)
      );
      this.options.selectedServices = preservedSelections.length > 0 
        ? preservedSelections 
        : [...this.options.services];
      return;
    }

    const selectedCategoryCodes = new Set<string>(
      selectedCategoryInputs
        .map((c) => (c.code || "").toString())
        .filter((code) => !!code)
    );

    const selectedCategories = this.options.categories.filter((cat) =>
      selectedCategoryCodes.has((cat.code || "").toString())
    );

    const selectedCategoryIds = new Set<number>(
      selectedCategories
        .map((s) => Number(s.categoryId))
        .filter((id) => !isNaN(id))
    );
    const selectedCategoryCodesNormalized = new Set<string>(
      selectedCategories
        .map((s) => (s.code || "").toString())
        .filter((code) => !!code)
    );

    this.options.filteredServices = this.options.services.filter((svc) => {
      const svcCategoryId = Number(svc.categoryId);
      const svcCategoryCode = (svc.categoryCode || "").toString();
      return (
        selectedCategoryIds.has(svcCategoryId) ||
        selectedCategoryCodesNormalized.has(svcCategoryCode)
      );
    });

    const preservedSelections = this.options.filteredServices.filter((svc) =>
      previousSelectedServices.some((selected) => selected.code === svc.code)
    );

    const newlySelectedCategoryIds = Array.from(selectedCategoryIds).filter(
      (id) => !previousFilteredCategoryIds.has(id)
    );
    const newlySelectedCategoryCodes = Array.from(
      selectedCategoryCodesNormalized
    ).filter(
      (code) => !previousFilteredCategoryCodes.has(code)
    );

    const newlySelectedServices = this.options.filteredServices.filter((svc) =>
      newlySelectedCategoryIds.includes(Number(svc.categoryId)) ||
      newlySelectedCategoryCodes.includes((svc.categoryCode || "").toString())
    );

    const mergedSelections = [...preservedSelections, ...newlySelectedServices];
    this.options.selectedServices = Array.from(
      new Map(mergedSelections.map((svc) => [svc.code, svc])).values()
    );
  }

  public filterAssistedByStaffBySupervisor(
    staff?: any,
    isAllSelected?: boolean
  ) {
    if (this.options.assistedBy) {
      let selectedSupervisors: SupervisorFilterOption[] = [];
      if (!this.options.filteredAssistedBys) {
        this.options.filteredAssistedBys = new Array<AssistedByFilterOption>();
      }
      this.options.selectedAssistedBys = [];
      this.options.filteredAssistedBys = [];
      if (isAllSelected) {
        this.options.filteredAssistedBys = this.options.assistedBy;
        this.options.selectedAssistedBys = this.options.filteredAssistedBys;
      } else {
        if (!staff) staff = this.options.selectedSupervisors;
        selectedSupervisors = this.options.supervisors.filter((c) =>
          staff.map((s: SupervisorFilterOption) => s.code).includes(c.code)
        );

        let filteredStaff = this.options.assistedBy.filter(
          (x) =>
            x.staffSupervisorsId != null &&
            selectedSupervisors
              .map((s) => s.staffId)
              .includes(x.staffSupervisorsId)
        );
        if (
          selectedSupervisors.find((s) => s.code == "UNASSIGNED_SUPERVISOR")
        ) {
          this.options.filteredAssistedBys = this.options.assistedBy.filter(
            (x) => x.staffSupervisorsId == null
          );
        }
        this.options.filteredAssistedBys = [
          ...this.options.filteredAssistedBys,
          ...filteredStaff,
        ];
        this.options.filteredAssistedBys.sort((a, b) =>
          a.staffFullname.localeCompare(b.staffFullname)
        );
        this.options.selectedAssistedBys = this.options.filteredAssistedBys;
      }
    }
  }

  public mapSharedOptionData() {
    // Get comma-separated IDs from currentTemplate
    const userIds = (this.currentTemplate.sharedUsers || "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id);

    const groupIds = (this.currentTemplate.sharedPermissionGroups || "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id);

    // Filter users and groups by matching IDs
    const matchedUsers = this.options.shareableUsers.filter((u) =>
      userIds.includes(String(u.staffId))
    );
    const matchedGroups = this.options.shareablePermissionGroups.filter((g) =>
      groupIds.includes(String(g.permissionGroupId))
    );

    // Return or assign as needed
    return {
      users: matchedUsers,
      groups: matchedGroups,
    };
  }

  public EnrichSelectedOptions() {
    if (this.options.selectedRequestedStaff && this.options.selectedRequestedStaff.length > 0) {
      this.options.selectedRequestedStaff = this.options.selectedRequestedStaff.map((sel) => {
        const matching = this.options.requestedStaff.find((opt) => opt.code === sel.code);
        return {
          ...sel,
          staffFullname: matching?.staffFullname || sel.staffFullname || '',
          localizedStaffFullname: matching?.localizedStaffFullname || sel.localizedStaffFullname || '',
        };
      });
    }

    if (this.options.selectedAssistedBys && this.options.selectedAssistedBys.length > 0) {
      this.options.selectedAssistedBys = this.options.selectedAssistedBys.map((sel) => {
        const matching = (this.options.filteredAssistedBys || this.options.assistedBy).find((opt) => opt.code === sel.code);
        return {
          ...sel,
          staffFullname: matching?.staffFullname || sel.staffFullname || '',
        };
      });
    }

    if (this.options.selectedSupervisors && this.options.selectedSupervisors.length > 0) {
      this.options.selectedSupervisors = this.options.selectedSupervisors.map((sel) => {
        const matching = this.options.supervisors.find((opt) => opt.code === sel.code);
        return {
          ...sel,
          staffFullname: matching?.staffFullname || sel.staffFullname || '',
          localizedStaffFullname: matching?.localizedStaffFullname || sel.localizedStaffFullname || '',
        };
      });
    }

    if (this.options.selectedUsers && this.options.selectedUsers.length > 0) {
      this.options.selectedUsers = this.options.selectedUsers.map((sel) => {
        const matching = this.options.users.find((opt) => opt.code === sel.code);
        return {
          ...sel,
          staffFullname: matching?.staffFullname || sel.staffFullname || '',
        };
      });
    }

    if (this.options.selectedDaysOfWeek && this.options.selectedDaysOfWeek.length > 0) {
      this.options.selectedDaysOfWeek = this.options.selectedDaysOfWeek.map((sel) => {
        const matching = this.options.daysOfWeek.find((opt) => opt.dayOfWeek === sel.dayOfWeek);
        return {
          ...sel,
          dayOfWeekName: matching?.dayOfWeekName || sel.dayOfWeekName || '',
          localizedDayOfWeekName: matching?.localizedDayOfWeekName || sel.localizedDayOfWeekName || '',
        };
      });
    }

    if (this.options.selectedStatuses && this.options.selectedStatuses.length > 0) {
      this.options.selectedStatuses = this.options.selectedStatuses.map((sel) => {
        const matching = this.options.statuses.find((opt) => opt.lobbyStatusId === sel.lobbyStatusId);
        return {
          ...sel,
          localizedStatusName: matching?.localizedStatusName || sel.localizedStatusName || '',
          statusName: matching?.statusName || sel.statusName || '',
        };
      });
    }

    if (this.options.selectedAssistTypes && this.options.selectedAssistTypes.length > 0) {
      this.options.selectedAssistTypes = this.options.selectedAssistTypes.map((sel) => {
        const matching = this.options.assistTypes.find((opt) => opt.assistTypeId === sel.assistTypeId);
        return {
          ...sel,
          localizedAssistTypeName: matching?.localizedAssistTypeName || sel.localizedAssistTypeName || '',
        };
      });
    }

    if (this.options.selectedCategories && this.options.selectedCategories.length > 0) {
      this.options.selectedCategories = this.options.selectedCategories.map((sel) => {
        const matching = this.options.categories.find((opt) => opt.code === sel.code);
        return {
          ...sel,
          categoryName: matching?.categoryName || sel.categoryName || '',
        };
      });
    }

    if (this.options.selectedServices && this.options.selectedServices.length > 0) {
      const source = this.options.filteredServices && this.options.filteredServices.length
        ? this.options.filteredServices
        : this.options.services;
      this.options.selectedServices = this.options.selectedServices.map((sel) => {
        const matching = source.find((opt) => opt.code === sel.code);
        return {
          ...sel,
          serviceName: matching?.serviceName || sel.serviceName || '',
        };
      });
    }

    if (this.options.selectedTimePeriod && (this.options.selectedTimePeriod as any[]).length > 0) {
      const selArr = Array.isArray(this.options.selectedTimePeriod)
        ? this.options.selectedTimePeriod
        : [this.options.selectedTimePeriod];
      this.options.selectedTimePeriod = selArr.map((sel) => {
        const matching = this.options.timePeriods.find((opt) => opt.detailViewTimePeriodId === sel.detailViewTimePeriodId);
        return {
          ...sel,
          timePeriodName: matching?.timePeriodName || sel.timePeriodName || '',
          detailViewTimePeriodId: matching?.detailViewTimePeriodId ?? sel.detailViewTimePeriodId,
        };
      });
    }

    if (this.options.selectedViews && this.options.selectedViews.length > 0) {
      this.options.selectedViews = this.options.selectedViews.map((sel) => {
        const matching = this.options.defaultViews.find((opt) => Number(opt.viewId) === Number((sel as any).viewId));
        return {
          ...sel,
          viewLocalizedName: matching?.viewLocalizedName || sel.viewLocalizedName || '',
          viewName: matching?.viewName || sel.viewName || '',
        };
      });
    }
  }
}
